import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Radar, Activity, Lock } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1764050445664-9d27c150c703?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxhZGFuaSUyMGNvcnBvcmF0ZSUyMGJ1aWxkaW5nfGVufDB8fHx8MTc3NjY2NDQzOHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@adani-intel.com");
  const [password, setPassword] = useState("Admin@123");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) nav("/dashboard", { replace: true });
  }, [user, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Authenticated. Welcome back.");
      nav("/dashboard", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || "Login failed";
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F8FAFC]" data-testid="login-page">
      {/* Left : imagery + brand */}
      <div className="relative hidden lg:block">
        <img src={BG} alt="corporate" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#020617]/85 via-[#020617]/70 to-[#0f172a]/50" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center">
              <img src="/logo.png" alt="CIMAP Logo" className="h-12 w-auto" />
            </div>
            <div>
              <div className="font-display text-lg font-bold tracking-tight">CIMAP</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">Intelligence Console</div>
            </div>
          </div>

          <div className="space-y-6 max-w-lg">
            <div className="kicker text-white/60">Centralized Intelligence Monitoring &amp; Analysis Platform</div>
            <h1 className="font-display text-5xl font-bold leading-[1.05] tracking-tight">
              Signals. Sentiment. Severity. <span className="text-[#60a5fa]">In one console.</span>
            </h1>
            <p className="text-sm text-white/70 max-w-md leading-relaxed">
              A real-time OSINT dashboard that ingests posts from X, Instagram, Facebook,
              LinkedIn and Reddit — enriches them with sentiment, risk and geospatial
              context — and surfaces narratives worth watching.
            </p>

            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/10">
              <Stat label="Ventures tracked" value="06" />
              <Stat label="Active sites" value="14" />
              <Stat label="Signals / hr" value="320+" />
            </div>
          </div>

          <div className="flex items-center gap-4 text-[11px] uppercase tracking-[0.22em] text-white/50">
            <span className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" /> SOC II-ready</span>
            <span>·</span>
            <span>Restricted access · Audit logged</span>
          </div>
        </div>
      </div>

      {/* Right : form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 lg:hidden mb-8">
            <img src="/logo.png" alt="CIMAP Logo" className="h-10 w-auto" />
            <div>
              <div className="font-display text-lg font-bold">CIMAP</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Intelligence Console</div>
            </div>
          </div>

          <div className="mb-8">
            <div className="kicker mb-3">Secure Analyst Sign-In</div>
            <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">
              Access the Intelligence Console
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Sign in with your corporate analyst credentials to continue.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Work Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-sm border-slate-300 h-11 font-mono text-sm"
                data-testid="login-email-input"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-sm border-slate-300 h-11 font-mono text-sm pl-9"
                  data-testid="login-password-input"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 rounded-sm bg-[#020617] hover:bg-[#0f172a] text-white tracking-wider uppercase text-xs font-semibold"
              data-testid="login-submit-button"
            >
              {submitting ? "Authenticating…" : "Enter the Console"}
            </Button>

            <div className="pt-4 border-t border-slate-200">
              <div className="kicker mb-2 text-slate-400">Demo credentials</div>
              <div className="font-mono text-xs text-slate-600 space-y-1">
                <div>admin@adani-intel.com · Admin@123</div>
                <div>analyst@adani-intel.com · Analyst@123</div>
              </div>
            </div>

            <div className="flex items-center gap-2 text-[11px] text-slate-400">
              <Activity className="h-3 w-3" /> Sessions are authenticated with JWT · HTTPS enforced
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-display text-3xl font-bold text-white">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 mt-1">{label}</div>
    </div>
  );
}
